<template>
    <div class="reach-brand-safety campaign-step">
        <div class="step-header">
            <div class="step-icon">
                <img src="/images/globe-lock.svg" alt="reach and brand safety settings" />
            </div>
            <h2>{{ $t('messages.reach_brand_safety') }}</h2>
        </div>

        <div class="settings-content">
            <!-- Кросс-частота -->
            <FrequencySettings ref="frequencySettings" :value="{
                frequency: localData.frequency,
                period_type: localData.period_type,
                period_value: localData.period_value,
                frequency_is_active: localData.frequency_is_active
            }" @update="updateFrequency" />

            <!-- Сегменты безопасности бренда -->
            <BrandSafetySegments :segments="brandSafetySegments" :selected-segments="localData.brandSafetySegments"
                @add-segment="addSegment" @remove-segment="removeSegment" @delete-all-segments="deleteAllSegments" />
        </div>
    </div>
</template>

<script>
import BrandSafetySegments from './ReachBrandSafety/BrandSafetySegments.vue';
import FrequencySettings from './ReachBrandSafety/FrequencySettings.vue';

export default {
    name: "ReachBrandSafety",
    components: {
        BrandSafetySegments,
        FrequencySettings
    },
    props: {
        campaign: {
            type: Object,
            default: () => ({})
        },
        // support receiving brandSafetySegments either as Array or semicolon-separated String
        brandSafetySegments: {
            type: [Array, String],
            default: () => []
        },
        blacklists: {
            type: Array,
            default: () => []
        },
        whitelists: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localData: {
                brandSafetySegments: [], // Теперь массив ID вместо объектов
                // single campaign-level frequency fields (kept here to emit)
                frequency: null,
                period_type: 'day',
                period_value: 1,
                frequency_is_active: true
            }
        };
    },
    mounted() {
        this.initializeData();
    },
    beforeUnmount() {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }
    },
    watch: {
        campaign: {
            handler(newCampaign) {
                if (newCampaign) {
                    this.initializeData();
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        initializeData() {
            if (this.campaign) {
                if (this.campaign.brandSafetySegments) {
                    this.localData.brandSafetySegments = this.mapBrandSafetySegmentsData(this.campaign.brandSafetySegments);
                }
                if (this.campaign.frequency !== undefined) {
                    this.localData.frequency = this.campaign.frequency ?? 1;
                    this.localData.period_type = this.campaign.period_type ?? 'day';
                    this.localData.period_value = this.campaign.period_value ?? 1;
                    this.localData.frequency_is_active = this.campaign.frequency_is_active ?? true;
                } else if (this.campaign.frequency_rules && Array.isArray(this.campaign.frequency_rules) && this.campaign.frequency_rules.length) {
                    const r = this.campaign.frequency_rules[0];
                    this.localData.frequency = r.frequency ?? 1;
                    this.localData.period_type = r.period_type ?? 'day';
                    this.localData.period_value = r.period_value ?? 1;
                    this.localData.frequency_is_active = r.is_active ?? true;
                } else {
                    this.localData.frequency = null;
                    this.localData.period_type = 'day';
                    this.localData.period_value = 1;
                    this.localData.frequency_is_active = true;
                }
            }
        },

        // mapBlocklistData removed

        mapBrandSafetySegmentsData(segmentData) {
            if (!segmentData) {
                return [];
            }

            // Если приходит как строка (ID через ;), преобразуем в массив числовых ID
            if (typeof segmentData === 'string') {
                return segmentData.split(';')
                    .filter(id => id.trim())
                    .map(id => parseInt(id.trim()))
                    .filter(id => !isNaN(id));
            } else if (Array.isArray(segmentData)) {
                // Если приходит массив объектов, извлекаем только ID и преобразуем в числа
                return segmentData.map(segment => {
                    const id = segment.id || segment;
                    return typeof id === 'number' ? id : parseInt(id);
                }).filter(id => !isNaN(id));
            }

            return [];
        },

        // Blocklist and environment handling removed; only brandSafetySegments remain

        // Методы для работы с сегментами безопасности бренда
        addSegment(segmentId) {
            if (!this.isSegmentSelected(segmentId)) {
                this.localData.brandSafetySegments.push(segmentId);
                this.updateData();
            }
        },

        removeSegment(segmentId) {
            const index = this.localData.brandSafetySegments.indexOf(segmentId);
            if (index > -1) {
                this.localData.brandSafetySegments.splice(index, 1);
                this.updateData();
            }
        },

        isSegmentSelected(segmentId) {
            return this.localData.brandSafetySegments.includes(segmentId);
        },

        deleteAllSegments() {
            this.localData.brandSafetySegments = [];
            this.updateData();
        },

        // Frequency update handler: receive object from child FrequencySettings
        updateFrequency(payload) {
            if (!payload || typeof payload !== 'object') return;
            this.localData.frequency = payload.frequency ?? this.localData.frequency;
            this.localData.period_type = payload.period_type ?? this.localData.period_type;
            this.localData.period_value = payload.period_value ?? this.localData.period_value;
            this.localData.frequency_is_active = payload.frequency_is_active ?? this.localData.frequency_is_active;
            this.updateData();
        },

        updateData() {
            // Emit immediately to avoid race conditions on save
            const dataToEmit = {
                // emit as array of ids; controller accepts array or semicolon string
                brandSafetySegments: this.localData.brandSafetySegments,
                frequency: this.localData.frequency,
                period_type: this.localData.period_type,
                period_value: this.localData.period_value,
                frequency_is_active: this.localData.frequency_is_active,

            };
            this.$emit('update', dataToEmit);
        },


        onError(message) {
            alert(message);
        },

        getCurrentFrequency() {
            // prefer value from child component if available
            try {
                if (this.$refs.frequencySettings && this.$refs.frequencySettings.getCurrentFrequency) {
                    return this.$refs.frequencySettings.getCurrentFrequency();
                }
            } catch (e) {
                // ignore and fallback
            }

            return {
                frequency: this.localData.frequency,
                period_type: this.localData.period_type,
                period_value: this.localData.period_value,
                frequency_is_active: this.localData.frequency_is_active
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.reach-brand-safety {
    max-width: 1000px;
}
</style>
